import { roleLabel, kindLabel } from './display-copy';
import { displayValue } from '../../lib/oem-display';
import { useState } from 'react';
import { LockKeyhole, AlertTriangle } from 'lucide-react';
import type { OemContext } from '../../lib/auth';
import type { OemRecord, OemWorkspace } from '../../lib/oem-client';
export function ReviewWorkspace({
  workspace,
  context,
  busy,
  onCommand,
  link,
}: {
  readonly workspace: OemWorkspace;
  readonly context: OemContext;
  readonly busy: boolean;
  readonly onCommand: (
    action: string,
    record: OemRecord | null,
    payload: Record<string, unknown>,
  ) => Promise<void>;
  readonly link: (page: string) => string;
}) {
  const [selected, setSelected] = useState(''),
    [comment, setComment] = useState(''),
    [disposition, setDisposition] = useState('pending'),
    [search, setSearch] = useState('');
  const owner = context.role === 'owner' || context.role === 'commercial_checker';
  const records = workspace.records.filter((r) =>
    owner
      ? ['policy', 'cost', 'quote', 'commitment', 'exception'].includes(r.kind)
      : [
          'formula',
          'process',
          'density',
          'feasibility',
          'applicability',
          'test_plan',
          'test_result',
          'artwork',
          'packaging',
          'hold',
        ].includes(r.kind),
  );
  const current = records.find((r) => r.id === selected) ?? records.at(-1);
  const reviewAction = owner
    ? 'review_commercial'
    : context.role === 'qa'
      ? 'review_regulatory'
      : 'review_technical';
  if (owner)
    return (
      <>
        <div className="page-title">
          <div>
            <div className="eyebrow">งานที่เจ้าของกิจการต้องพิจารณา</div>
            <h1>
              ตัดสินใจเรื่องธุรกิจ
              <br />
              โดยเห็นผลกระทบครบ
            </h1>
            <p className="muted wrap">
              {workspace.project.label} · {workspace.project.brand}
            </p>
          </div>
          <span className="chip">
            <LockKeyhole />
            เฉพาะอำนาจที่ได้รับ
          </span>
        </div>
        <div className="owner-grid">
          <section>
            <div className="card" style={{ padding: 0 }}>
              <div style={{ padding: '20px 20px 0' }}>
                <h2>เรื่องสำหรับพิจารณา</h2>
                <p className="muted">วงเงินและนโยบายใช้เฉพาะหลักฐานที่ได้รับการทบทวน</p>
              </div>
              {[
                {
                  number: '01 / นโยบายธุรกิจ',
                  title: 'รอบตัวอย่างและเครดิตเมื่อสั่งผลิต',
                  text: 'จำนวนรอบ ค่ารอบเพิ่ม และเงื่อนไขเครดิตต้องมีนโยบายที่ระบุผู้รับผิดชอบและเวอร์ชัน',
                  page: 'policies',
                  button: 'ดูเงื่อนไขที่ต้องกำหนด',
                },
                {
                  number: '02 / ภาระผูกพันบรรจุภัณฑ์',
                  title: 'ภาระผูกพันก่อนผลิต',
                  text: 'ต้องระบุชิ้นส่วนและแบบฉลากและบรรจุภัณฑ์ที่ใช้จริง จำนวน วงเงิน เวลา และความเสี่ยงเมื่อยกเลิก แยกจากการยอมรับราคา',
                  page: 'commitments',
                  button: 'ดูภาระผูกพันและผลกระทบ',
                },
                {
                  number: '03 / ความพร้อมออกใบเสนอราคา',
                  title: displayValue(
                    workspace.records.filter((r) => r.kind === 'quote').at(-1)?.content.label ??
                      'ยังไม่มีใบเสนอราคา',
                  ),
                  text: 'ราคาวัตถุดิบ อัตราได้ผลผลิต และความหนาแน่นต้องมีหลักฐาน ไม่ใช้ข้อมูลที่ยังไม่ทราบคำนวณกำไรเพื่อตัดสินใจ',
                  page: 'quotes',
                  button: 'ดูรายการข้อมูลที่ขาด',
                },
              ].map((d) => (
                <article className="decision-card" key={d.number}>
                  <div className="number">{d.number}</div>
                  <span className="chip amber">รอหลักฐานครบ</span>
                  <h3>{d.title}</h3>
                  <p>{d.text}</p>
                  <div className="between">
                    <span className="muted">ขอบเขตธุรกิจ · ตรวจฉบับและหลักฐานก่อนตัดสินใจ</span>
                    <a className="action secondary" href={link(d.page)}>
                      {d.button}
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </section>
          <aside className="stack">
            <div className="card">
              <div className="eyebrow">โครงการนี้</div>
              <h2>{workspace.project.label}</h2>
              <figure className="photo-large">
                <img
                  src="/concepts/cream-packaging-concept-800.webp"
                  srcSet="/concepts/cream-packaging-concept-400.webp 400w, /concepts/cream-packaging-concept-800.webp 800w, /concepts/cream-packaging-concept-1200.webp 1200w"
                  sizes="(max-width: 768px) 100vw, 400px"
                  loading="lazy"
                  decoding="async"
                  alt="ภาพแนวคิด AI ไม่ใช่ผลิตภัณฑ์จริง"
                  width="1536"
                  height="1024"
                />
                <figcaption className="caption">
                  ภาพแนวคิดจาก AI · ไม่ใช่สินค้า SQI หรือตัวอย่างจริง
                </figcaption>
              </figure>
              {workspace.skus.map((s) => (
                <div className="detail-row" key={s.id}>
                  <span>{s.label}</span>
                  <span>เป้าหมายของแต่ละ SKU</span>
                </div>
              ))}
              <div className="detail-row">
                <span>สถานะรวม</span>
                <span>{workspace.order ? 'ข้อมูลธุรกิจที่ยืนยันไว้' : 'ก่อนยืนยันข้อมูล'}</span>
              </div>
            </div>
            <div className="card">
              <h3>เงื่อนไขที่ต้องผ่านตามหน้าที่</h3>
              {[
                ['เงื่อนไขที่ยังไม่ผ่านด้านคุณภาพและข้อกำหนด', 'reviews'],
                ['ตรวจสอบ PO', 'po'],
                ['หลักฐานการชำระเงิน', 'payments'],
              ].map(([label, page]) => (
                <div className="statusline" key={label}>
                  <span>{label}</span>
                  <a className="chip amber" href={link(page ?? 'home')}>
                    ตรวจหลักฐาน
                  </a>
                </div>
              ))}
              <div className="scope">
                เจ้าของกิจการตัดสินใจได้เฉพาะขอบเขตธุรกิจ
                <br />
                ไม่สามารถข้ามข้อกำหนดกฎหมาย ความปลอดภัย และคุณภาพ
              </div>
            </div>
            <div className="note-warning">
              การยืนยันสร้างชุดข้อมูลและข้อมูลส่งต่อเท่านั้น ไม่สั่งซื้อ ไม่อนุญาตผลิต
              และไม่อนุมัติปล่อยสินค้าแต่ละล็อต
            </div>
          </aside>
        </div>
      </>
    );
  return (
    <>
      <div className="page-title">
        <div>
          <div className="eyebrow">ตรวจข้อกำหนดและหลักฐาน</div>
          <h1>ตรวจหลักฐานให้ตรงเวอร์ชัน</h1>
          <p className="muted wrap">
            {workspace.project.label} · {workspace.project.brand}
          </p>
        </div>
        <span className="chip">
          <LockKeyhole />
          ทีมงาน · {roleLabel(context.role)}
        </span>
      </div>
      <div className="review-layout">
        <aside className="queue-panel">
          <div className="between">
            <strong>คิวที่มอบหมาย</strong>
            <span className="chip">{records.length} เวอร์ชัน</span>
          </div>
          <label>
            กรองคิว
            <input value={search} onChange={(e) => setSearch(e.target.value)} />
          </label>
          {records
            .filter((r) => displayValue(r.content.label).includes(search))
            .map((r) => (
              <button
                key={r.id}
                className={`queue-item ${current?.id === r.id ? 'active' : ''}`}
                onClick={() => setSelected(r.id)}
              >
                <strong>{displayValue(r.content.label)}</strong>
                {kindLabel(r.kind)} v{r.version}
                <br />
                <span className="chip amber">
                  {workspace.decisions.filter((d) => d.recordId === r.id).length} การตรวจ
                </span>
              </button>
            ))}
          <p className="mock-note">คงคิวและตัวกรองระหว่างตรวจ</p>
        </aside>
        <section className="stack">
          <div className="card">
            <div className="between">
              <h2>ตรวจความพร้อมตามหลักฐาน</h2>
              <span className="chip amber">ตรวจรายการฉบับที่เลือก</span>
            </div>
            <p className="version">
              {current
                ? `${kindLabel(current.kind)} · ${displayValue(current.content.label)} v${current.version} · ${current.hash}`
                : 'ยังไม่มีหลักฐานที่อยู่ในขอบเขต'}
            </p>
            <div className="note-warning">
              DEMO — NOT FOR REGULATORY USE — ไม่ใช่ข้อมูลสำหรับใช้ยื่นจริง
            </div>
            <div className="issue">
              <div className="issue-title">
                <AlertTriangle />
                <span>ส่วนประกอบวัตถุดิบผสมต้องใช้ฐานความเข้มข้นที่เข้ากัน</span>
              </div>
              <p>
                2% × 10% = 0.2% ของสารสำคัญ คำนวณได้เฉพาะฐานความเข้มข้นที่เข้ากัน
                ข้อมูลส่วนที่เหลือยังไม่ทราบจนมีหลักฐาน
              </p>
              <a href={link('formulas')}>ตรวจสูตรและส่วนประกอบ</a>
            </div>
            <div className="issue">
              <h3>ตรวจเงื่อนไขการทดสอบและรายงานผล</h3>
              <p>
                ผู้ตรวจที่รับผิดชอบกำหนดการทดสอบและเกณฑ์
                ผลที่ส่งเข้าระบบต้องผ่านการตรวจและยอมรับโดยผู้รับผิดชอบ
              </p>
              <a href={link('tests')}>ตรวจเกณฑ์ ผล และรายการฉบับที่เลือก</a>
            </div>
            <div className="issue">
              <h3>ตรวจข้อความบนฉลากและแบบตัด</h3>
              <p>คำกล่าวอ้างและข้อมูลฉลากต้องผ่านการตรวจตามหน้าที่ ลูกค้ารับดีไซน์เป็นอีกขอบเขต</p>
              <a href={link('artwork')}>เปิดแบบฉบับที่เลือก</a>
            </div>
          </div>
          <div className="card">
            <h3>การตรวจทานและการบันทึก</h3>
            <div className="audience">สำหรับทีมงานเท่านั้น · ไม่ส่งบันทึกภายในให้ลูกค้า</div>
            <label>
              ผลการตรวจ
              <select value={disposition} onChange={(e) => setDisposition(e.target.value)}>
                <option value="pending">รอหลักฐาน</option>
                <option value="passed">ผ่านตามหลักฐาน</option>
                <option value="failed">ไม่ผ่าน</option>
                <option value="not_applicable">ไม่เกี่ยวข้อง · ต้องมีเหตุผล</option>
              </select>
            </label>
            <label>
              หลักฐานและเหตุผล
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                required
                maxLength={4000}
              />
            </label>
            <div className="footer-actions">
              <p className="mock-note">ข้อกำหนดที่ยังไม่ผ่านต้องแก้ไข เจ้าของกิจการข้ามไม่ได้</p>
              <div className="actions">
                <a className="action secondary" href={link('documents')}>
                  ตรวจเอกสาร
                </a>
                <button
                  className="action"
                  disabled={!current || busy || !comment || !context.actions.includes(reviewAction)}
                  onClick={() =>
                    current && void onCommand(reviewAction, current, { disposition, comment })
                  }
                >
                  บันทึกการตรวจ
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
